import { useMemo } from 'react';
import {
  getCurrentBranding,
  getCurrentPatronId,
  getBrandingValue,
  getImages,
  getColors,
  getCompanyInfo,
  getAppConfig,
  getTypography,
  getCSSCustomProperties,
  getBrandedStyles,
  getBrandedClassName
} from '../utils/brandingUtils';

/**
 * React hook for accessing branding configuration
 * Provides all branding utilities with React optimization
 * @returns {Object} Branding configuration and utilities
 */
export const useBranding = () => {
  // Memoize branding data to prevent unnecessary re-renders
  const branding = useMemo(() => {
    const currentBranding = getCurrentBranding();
    return {
      // Raw branding data
      config: currentBranding,
      patronId: getCurrentPatronId(),
      
      // Specific configurations
      images: getImages(),
      colors: getColors(),
      company: getCompanyInfo(),
      app: getAppConfig(),
      typography: getTypography(),
      
      // CSS utilities
      cssProperties: getCSSCustomProperties(),
      
      // Utility functions
      getValue: getBrandingValue,
      getStyles: getBrandedStyles,
      getClassName: getBrandedClassName
    };
  }, []);

  return branding;
};

/**
 * Hook specifically for getting branded image sources
 * @returns {Object} Image source getters
 */
export const useBrandedImages = () => {
  const { images } = useBranding();
  
  return useMemo(() => ({
    logo: images.logo,
    sidebarExpanded: images.sidebarExpanded,
    sidebarCollapsed: images.sidebarCollapsed,
    loginLogo: images.loginLogo,
    favicon: images.favicon,
    appleTouchIcon: images.appleTouchIcon,
    arrowDefault: images.arrowDefault,
    arrowWhite: images.arrowWhite,
    arrowBlack: images.arrowBlack,
    
    // Helper function to get any image by key
    getImage: (key) => images[key] || null,
    
    // Helper function to get sidebar logo based on state
    getSidebarLogo: (isOpen) => isOpen ? images.sidebarExpanded : images.sidebarCollapsed
  }), [images]);
};

/**
 * Hook specifically for getting branded colors
 * @returns {Object} Color palette and utilities
 */
export const useBrandedColors = () => {
  const { colors } = useBranding();
  
  return useMemo(() => ({
    ...colors,
    
    // Helper functions for common color operations
    getPrimary: (opacity = 1) => opacity === 1 ? colors.primary : `${colors.primary}${Math.round(opacity * 255).toString(16)}`,
    getSecondary: (opacity = 1) => opacity === 1 ? colors.secondary : `${colors.secondary}${Math.round(opacity * 255).toString(16)}`,
    
    // Status color mappings
    status: {
      success: colors.success,
      warning: colors.warning,
      error: colors.error,
      info: colors.info
    }
  }), [colors]);
};

/**
 * Hook specifically for getting branded styles
 * @returns {Object} Style generators
 */
export const useBrandedStyles = () => {
  const { getStyles, colors, typography } = useBranding();
  
  return useMemo(() => ({
    // Pre-built common styles
    button: {
      primary: getStyles('button', 'primary'),
      secondary: getStyles('button', 'secondary')
    },
    
    background: {
      primary: getStyles('background', 'primary'),
      secondary: getStyles('background', 'secondary'),
      gradient: getStyles('background', 'gradient')
    },
    
    text: {
      primary: getStyles('text', 'primary'),
      secondary: getStyles('text', 'secondary')
    },
    
    // Custom style generators
    getButtonStyle: (variant = 'primary', customStyles = {}) => ({
      ...getStyles('button', variant),
      ...customStyles
    }),
    
    getBackgroundStyle: (variant = 'primary', customStyles = {}) => ({
      ...getStyles('background', variant),
      ...customStyles
    }),
    
    // Common layout styles
    sidebar: {
      backgroundColor: colors.sidebarBg,
      fontFamily: typography.fontFamily
    },
    
    mainContent: {
      backgroundColor: colors.mainBg,
      fontFamily: typography.fontFamily
    },
    
    contentArea: {
      backgroundColor: colors.contentBg,
      fontFamily: typography.fontFamily
    }
  }), [getStyles, colors, typography]);
};

/**
 * Hook for company branding information
 * @returns {Object} Company information and branded text
 */
export const useCompanyBranding = () => {
  const { company, app } = useBranding();
  
  return useMemo(() => ({
    ...company,
    ...app,
    
    // Helper functions for common text needs
    getPageTitle: (pageTitle) => `${pageTitle} - ${app.title}`,
    getFullTitle: () => `${company.name} - ${app.title}`,
    getBrowserTitle: () => `${app.title} | ${company.fullName}`
  }), [company, app]);
};

export default useBranding; 